import usePaymentOrderOptions from "../../gestion-payments/usePaymentOrderOptions";
import usePaymentFormChoices from "../../gestion-payments/usePaymentFormChoices";
import BusinessResourceNotice from "../../components/BusinessResourceNotice";
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  message,
  Spin,
  Row,
  Col,
  Alert,
  Space,
  Card,
  Descriptions,
  Progress,
} from "antd";
import dayjs from "dayjs";
import axios from "axios";
import { PaymentFormModalProps } from "../types";
import apiClient from "../../api/apiClient";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import { useCommercialTreasuryAccountSuggestion } from "../../hooks/useCommercialTreasurySuggestion";

const { Option } = Select;

// Format numbers for French locale
const formatNumber = (value: any): string => {
  if (value === null || value === undefined) return "0";
  const num =
    typeof value === "string"
      ? parseFloat(value.replace(/\s/g, ""))
      : Number(value);
  if (isNaN(num)) return "0";
  return num
    .toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
    .replace(/ /g, "\u00A0"); // non-breaking space
};

// Generate a unique idempotency key
const generateIdempotencyKey = (): string => {
  return `payment-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
};

const PaymentFormModal: React.FC<PaymentFormModalProps> = ({
  visible,
  onClose,
  order,
  onPaymentAdded,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user, hasPermission } = useAuth();
  const canCreateOutgoingPayment = hasPermission(
    "Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.create"
  );
  // Prevent duplicate submissions
  const inFlight = useRef(false);
  const idemKeyRef = useRef<string | null>(null);

  const openedAt = useRef(0);
  const companyId = order?.company_id || selectedCompany;
  const warehouseId = order?.warehouse_id || selectedWarehouse;
  const contextMatches = (!selectedCompany || Number(companyId) === Number(selectedCompany)) &&
    (!selectedWarehouse || Number(warehouseId) === Number(selectedWarehouse));
  const balances = usePaymentOrderOptions({ userId: user?.id, companyId, warehouseId,
    paymentType: "out", entityId: order?.user_id, orderId: order?.id,
    active: visible, enabled: canCreateOutgoingPayment && contextMatches });
  const choices = usePaymentFormChoices({ userId: user?.id, companyId, warehouseId,
    paymentType: "out", includeEntities: false, active: visible,
    enabled: canCreateOutgoingPayment && contextMatches });
  const { paymentModes, treasuryAccounts } = choices;
  const loadingModes = choices.loading;
  const loadingAccounts = choices.loading;
  const freshOrder = balances.orders[0];
  const remaining = freshOrder?.due_amount ?? 0;
  const formattedRemaining = freshOrder ? formatNumber(remaining) : "Vérification…";
  const fullyPaid = Boolean(freshOrder && remaining <= 0);
  const percentagePaid = freshOrder && freshOrder.total > 0
    ? Math.round((freshOrder.paid_amount / freshOrder.total) * 100) : 0;

  const { onModeChange: suggestTreasuryAccount } =
    useCommercialTreasuryAccountSuggestion(form, {
      warehouseId: order?.warehouse_id || selectedWarehouse,
      enabled: visible,
      modeField: "mode_paiement",
      accountField: "treasury_account_id",
      accounts: treasuryAccounts,
    });

  // Helper to compute payment status based on paid vs total
  const calculatePaymentStatus = (paid: number, total: number): string => {
    if (paid <= 0) return "Non payé";
    if (paid < total) return "Partiellement payé";
    return "Payé";
  };

  useEffect(() => {
    if (visible && order?.id) {
      openedAt.current = Date.now();
      setError(null);
      idemKeyRef.current = generateIdempotencyKey();
      form.resetFields();
      form.setFieldsValue({ date_paiement: dayjs() });
    }
  }, [visible, order?.id, user?.id, companyId, warehouseId, form]);
  useEffect(() => {
    if (visible && !balances.loading && balances.updatedAt >= openedAt.current && freshOrder && form.getFieldValue("montant") == null &&
      !form.isFieldTouched("montant")) form.setFieldsValue({ montant: freshOrder.due_amount });
  }, [visible, freshOrder, balances.loading, balances.updatedAt, form]);

  const onFinish = async (values: any) => {
    console.log("[PaymentFormModal] onFinish values:", values);
    if (!canCreateOutgoingPayment) {
      message.error(
        "Vous n'avez pas la permission d'ajouter un paiement fournisseur."
      );
      return;
    }
    if (!order || !order.id) {
      setError("Données de commande invalides.");
      return;
    }
    if (fullyPaid) {
      message.warning("Cette commande est déjà entièrement payée.");
      setError("Commande déjà soldée.");
      return;
    }
    if (inFlight.current) return;

    inFlight.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const amountVal = Number(values.montant);
      await balances.verify([{ order_id: order.id, amount: amountVal }]);
      const scopedCompanyId = companyId;
      const scopedWarehouseId = warehouseId;

      // Prepare payload including order update data
      const payload = {
        payment: {
          company_id: scopedCompanyId,
          warehouse_id: scopedWarehouseId,
          payment_type: "out",
          date: dayjs(values.date_paiement).format("YYYY-MM-DD"),
          amount: amountVal,
          payment_mode_id: values.mode_paiement,
          treasury_account_id: values.treasury_account_id,
          user_id: order.user_id,
          notes: values.remarques || "",
          staff_user_id: user?.id || null,
        },
        order: { id: order.id },
      };

      const res = await apiClient.post(
        "/api/payments/process-order-payment",
        payload,
        {
          headers: { "X-Idempotency-Key": idemKeyRef.current! },
          timeout: 20000,
        }
      );

      if (res.status === 200 || res.status === 201) {
        if (res.data.is_duplicate) {
          message.warning(`Paiement (${res.data.payment_number}) déjà traité.`);
        } else {
          message.success("Paiement ajouté avec succès");
        }
        void balances.invalidate();
        form.resetFields();
        onClose();
        if (onPaymentAdded) await onPaymentAdded(true);
      } else {
        throw new Error(`Réponse inattendue: ${res.status}`);
      }
    } catch (err: any) {
      console.error("Error submitting payment:", err);
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.error || err.message
        : err.message;
      setError(msg);
      if (err.response?.data?.code === "ORDER_PAYMENT_BALANCE_CHANGED") {
        void balances.refresh().catch(() => {});
      }
      message.error(msg);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Ajouter un paiement - ${order?.invoice_number || ""}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Spin spinning={submitting} tip="Enregistrement...">
        {!contextMatches && <Alert type="warning" showIcon message="Le contexte a changé. Fermez puis rouvrez le formulaire." />}
        <BusinessResourceNotice resource={balances} label="commandes et soldes" />
        <BusinessResourceNotice resource={choices} label="listes de choix" />
        {/* Payment summary */}
        {order && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Total">
                {freshOrder ? formatNumber(freshOrder.total) : "Vérification…"} XOF
              </Descriptions.Item>
              <Descriptions.Item label="Payé">
                {freshOrder ? formatNumber(freshOrder.paid_amount) : "Vérification…"} XOF
              </Descriptions.Item>
              <Descriptions.Item label="Restant dû">
                {formattedRemaining} XOF
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                {freshOrder ? calculatePaymentStatus(freshOrder.paid_amount, freshOrder.total) : "Vérification…"}
              </Descriptions.Item>
            </Descriptions>
            <Progress
              percent={percentagePaid}
              showInfo
              strokeColor={percentagePaid === 100 ? "#52c41a" : undefined}
            />
          </Card>
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={submitting}
        >
          {choices.status === "verified" && treasuryAccounts.length === 0 && (
            <Alert
              message="Aucun compte de trésorerie"
              description="Créez un compte de trésorerie pour enregistrer ce paiement."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {error && (
            <Alert
              message="Erreur"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="montant"
                label={`Montant à payer (Restant dû: ${formattedRemaining} XOF)`}
                rules={[
                  {
                    required: !fullyPaid,
                    message: "Veuillez entrer un montant",
                  },
                  {
                    validator(_, value) {
                      if (value <= 0) {
                        return Promise.reject(
                          new Error("Le montant doit être positif")
                        );
                      }
                      if (value > remaining) {
                        return Promise.reject(
                          new Error(`Max ${formattedRemaining} XOF`)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber<number>
                  style={{ width: "100%" }}
                  min={0.01}
                  formatter={(val) =>
                    `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0")
                  }
                  parser={(val) => (val ? Number(val.replace(/\s/g, "")) : 0)}
                  addonAfter="XOF"
                  disabled={fullyPaid}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="date_paiement"
                label="Date de paiement"
                rules={[
                  { required: true, message: "Veuillez choisir une date" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="mode_paiement"
            label="Mode de paiement"
            rules={[
              { required: true, message: "Veuillez sélectionner un mode" },
            ]}
          >
            <Select
              placeholder="Choisir un mode"
              loading={loadingModes}
              onChange={suggestTreasuryAccount}
            >
              {paymentModes.map((mode) => (
                <Option key={mode.id} value={mode.id}>
                  {mode.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="treasury_account_id"
            label="Compte de trésorerie"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner un compte de trésorerie",
              },
            ]}
          >
            <Select
              placeholder="Sélectionner un compte"
              loading={loadingAccounts}
              disabled={treasuryAccounts.length === 0}
            >
              {treasuryAccounts.map((account: any) => (
                <Option key={account.id} value={account.id}>
                  {account.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="remarques" label="Remarques (optionnel)">
            <Input.TextArea rows={3} placeholder="Note..." />
          </Form.Item>
          <Form.Item style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={onClose} disabled={submitting}>
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={
                  submitting || fullyPaid || !canCreateOutgoingPayment || !contextMatches || !balances.data || balances.loading || !choices.data
                }
              >
                Enregistrer
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default PaymentFormModal;
